import { FRAC_ONE, type Fraction } from '../util/fractionType';
import { approximateFractionValue } from '../util/integerMath';
import type { FilterableOutputProfile } from './filterableOutputProfile';
import type { FieldOrder, Resolution } from './types';

export function generateWorkingGenericProfiles() {
  return generateGenericH264Profiles();
}

export function generateGenericH264Profiles(): FilterableOutputProfile[] {
  const resolutions : Resolution[] = [
    { width: 1280, height: 720, },
    { width: 1920, height: 1080, },
    { width: 608, height: 1080 },
    { width: 1080, height: 1080 },
    { width: 1080, height: 1920 },
    { width: 4096, height: 2160 },
    { width: 2160, height: 4096 },
    { width: 3840, height: 2160 },
    { width: 2160, height: 3840 },
  ];

  const frameRates : Fraction[] = [
    { numerator: 24, denominator: 1 },
    { numerator: 25, denominator: 1 },
    { numerator: 30000, denominator: 1001 },
    { numerator: 30, denominator: 1 },
    { numerator: 50, denominator: 1 },
    { numerator: 60000, denominator: 1001 },
    { numerator: 60, denominator: 1 },
  ];

  const fieldOrders : FieldOrder[] = ['progressive', 'top_field_first'];

  const profiles : FilterableOutputProfile[] = [];
  for (const resolution of resolutions) {
    for (const frameRate of frameRates) {
      for (const fieldOrder of fieldOrders) {
        if (fieldOrder === 'top_field_first') {
          // Interlaced only makes sense for 1920*1080
          if (resolution.height !== 1080 || resolution.width !== 1920) {
            continue;
          }

          // Interlaced only makes sense for 25, 29.97, and 30.0 fps
          const fps = approximateFractionValue(frameRate);
          if (fps < 25.0 || fps > 30) {
            continue;
          }
        }
        profiles.push({
          type: 'generic',
          container: {
            type: 'single_file',
            format: { name: 'mp4', faststart: false },
          },
          video: {
            codec: {
              codec: 'h264',
              profile: 'main',
              gopSize: Math.round(frameRate.numerator / frameRate.denominator),
              bFrames: 2,
              rateControl: {
                type: 'crf',
                crf: 18,
              }
            },
            resolution: {
              width: resolution.width,
              height: resolution.height,
              sar: FRAC_ONE,
            },
            frameRate,
            fieldOrder,
            pixelFormat: 'yuv420p',
          },
          audio: {
            codec: {
              codec: 'aac',
              bitrate: 192000,
            },
            channelLimits: {
              min: 2,
              max: 2,
              defaultMix: false,
            },
            audioChannelsPerStream: 2,
            sampleRate: 48000
          }
        });
      }
    }
  }
  return profiles;
}

